import React, { forwardRef, useImperativeHandle, useCallback, useEffect, useState } from 'react';
import { Form } from '@block/plug';
import { datejs } from '@block/utils';
import { UploadCertificate, UploadCertificateRef } from '../UploadCertificate';
import { EduType, CertificateType, CertificateCodeItemKeyType } from '../../types';
import {
  EduTypeCertificateConfig,
  EducationType,
  AttachmentFileTypeMap,
  CertificateTypeAttachmentMap,
  EduTypeCertificateConfigArray,
} from '../../consts';
import {
  EDUCATION_LEVEL,
  REASON_INDEX,
  REASON_TYPE_REVERSE_MAP,
  SPECIAL_ATTACHMENT_FILE_TYPE,
} from '../UploadCertificate/constants';

interface CertificateUploadGroupProps {
  eduType: EduType;
  data: DefaultAny;
  mode?: 'view' | 'edit';
  onChange?: (code: string, value: any, type?: 'reset' | 'init') => void;
  labelWidth?: string;
  rightOffset?: number;
  // 无法上传原因列表
  unableReasonList?: any[];
}

export interface CertificateUploadGroupRef {
  validate: () => Promise<boolean>;
  getFormData: () => any;
  reset: () => void;
}

const CertificateUploadGroup = forwardRef<CertificateUploadGroupRef, CertificateUploadGroupProps>((props, ref) => {
  const {
    eduType,
    data,
    mode = 'edit',
    onChange,
    labelWidth = '120px',
    rightOffset = 2,
    // 无法上传原因列表
    unableReasonList,
  } = props;
  const { educationTypeDictIndex, attachmentList, diplomaDictIndex, endDate } = data || {};
  // 判断是否为全日制
  const isFullTime = educationTypeDictIndex === EducationType.FULL_TIME;

  // 添加一个状态来跟踪教育类型（全日制/非全日制）
  const [educationTypeState, setEducationTypeState] = useState<string>(educationTypeDictIndex);
  // 添加一个key来强制重新渲染组件
  const [renderKey, setRenderKey] = useState<number>(0);

  const firstRef = React.useRef<UploadCertificateRef>(null);
  const secondRef = React.useRef<UploadCertificateRef>(null);
  const thirdRef = React.useRef<UploadCertificateRef>(null);

  /**
   * 验证证书上传是否成功
   */
  const validate = async () => {
    try {
      const results = await Promise.all(
        [firstRef.current?.validate(), secondRef.current?.validate(), thirdRef.current?.validate()].filter(Boolean),
      );
      return results.every((result) => result);
    } catch (error) {
      return false;
    }
  };

  /**
   * 获取证书上传数据
   */
  const getFormData = useCallback(async () => {
    // getFormData 中fileType 是实时更新的，所以需要单独获取，通过hooks内部更新
    const firstFileData = await firstRef.current?.getFormData();
    const secondFileData = await secondRef.current?.getFormData();
    const thirdFileData = await thirdRef.current?.getFormData();

    // 构建附件列表
    const buildAttachmentList = (fileData: any) => {
      const { fileId, fileName, fileUrl, unableUpload, reason, attachmentFileType } = fileData || {};

      let reasonObj = {};
      // 只有当无法上传时，才需要上传原因
      if (unableUpload) {
        const { reasonType, reasonIndex } = reason || {};
        reasonObj = {
          reasonType,
          unableUploadReason: reasonIndex,
        };
      }

      return {
        attachmentId: fileId,
        attachmentName: fileName,
        attachmentUrl: fileUrl,
        attachmentFileType,
        // 有则上传，否则undefined
        unableUpload,
        ...reasonObj,
      };
    };

    const attachmentListData = [];
    // 初始化证书编码为null，确保每次都会被重置
    let educationCodeFormData = null;
    let degreeCodeFormData = null;

    // 获取当前教育类型的证书配置
    const certificateConfig = EduTypeCertificateConfig[eduType];

    // 处理文件数据和证书类型的通用函数
    const processFileData = (fileData, config) => {
      if (!fileData || !config) return null;

      const attachment = buildAttachmentList(fileData);
      attachmentListData.push(attachment);

      // 勾选无法上传就不需要提取编码
      if (fileData.unableUpload) {
        return attachment;
      }

      const { certificateType } = config;

      // 提取证书编码
      if (
        certificateType === CertificateType.Diploma &&
        fileData[CertificateCodeItemKeyType[CertificateType.Diploma]]
      ) {
        // 获取毕业证书编码
        educationCodeFormData = fileData[CertificateCodeItemKeyType[CertificateType.Diploma]];
      } else if (
        certificateType === CertificateType.Degree &&
        fileData[CertificateCodeItemKeyType[CertificateType.Degree]]
      ) {
        // 获取学位证书编码
        degreeCodeFormData = fileData[CertificateCodeItemKeyType[CertificateType.Degree]];
      }

      return attachment;
    };

    // 处理三个文件数据
    if (certificateConfig) {
      processFileData(firstFileData, certificateConfig.first);
      processFileData(secondFileData, certificateConfig.second);
      processFileData(thirdFileData, certificateConfig.third);
    }

    // 返回结果，将证书编码放在外层
    const result: any = {
      attachmentList: attachmentListData,
      // 始终包含这两个字段，确保每次都会更新，没有值时为null
      educationCode: educationCodeFormData,
      degreeCode: degreeCodeFormData,
    };

    return result;
  }, [eduType]);

  /**
   * 重置证书上传
   */
  const reset = () => {
    try {
      firstRef.current?.reset();
      secondRef.current?.reset();
      thirdRef.current?.reset();
    } catch (error) {
      console.error(error);
    }
  };

  const handleChange = (value: any) => {
    onChange?.('CertificateUploadGroup', value);
  };

  /**
   * 渲染证书上传组件
   */
  const renderUploads = () => {
    // 增加判断，减少不必要的渲染
    // 如果state内的educationTypeState还没及时更新，可以暂时不渲染
    // 等待useEffect更新后，与后端数据或者父组件数据保持一致后，再进行更新
    if (educationTypeDictIndex !== educationTypeState) {
      // 返回一个空的div作为占位符，保持与实际内容相同的高度和样式，避免闪屏
      return (
        <Form.Item>
          <div style={{ visibility: 'hidden', height: 'auto', width: '100%' }} />
        </Form.Item>
      );
    }

    // 非最高学历，不显示证书上传
    if (eduType === EduType.NotTopDegree) {
      return null;
    }

    // 如果educationTypeDictIndex为null，咋取消展示，比如点击了教育性质选框的清空按钮
    if (educationTypeDictIndex === null) {
      // 如果清空了教育性质，需要清空表单，并且不渲染
      handleChange(null);
      return null;
    }

    // 第一定位逻辑：根据 eduType 确定该教育类型应该展示哪些文件，一共6种情况，每一种情况对应的文件坑位数量不同，但是是固定的
    // 1、 eduType 是 EduType.InternChinese ，那么只有一个文件，文件类型是 CertificateType.OnlineVerification
    // 2、 eduType 是 EduType.InternForeign ，那么只有一个文件，文件类型是 CertificateType.StudyProof
    // 3、 eduType 是 EduType.ChineseCollege ，那么有两个文件，并且是有顺序的，文件类型分别是 CertificateType.Diploma 和 CertificateType.Professional 等

    // 第二定位逻辑：在当前 eduType 教育类型下，第一个文件坑已经确定展示毕业证类型，那么根据 attachmentFileType 对号入座，找到对应的文件类型的数据
    // 比如毕业证的 attachmentFileType 为 全日制 15、101、102，非全日制 111、113、114，那么循环 attachmentList 中的元素，filter出来对应的数据，应该只会有唯一的一个元素（与后端协商过）
    // 比如此时会拿到 15或者101 ，就正常展示该数据下的内容，有勾选，就对应回显，不管是不是全日制，都只会有一种

    // 第三定位逻辑：
    // 1、如果 attachmentFileType 为空，说明选择了无法上传，也勾选了原因，但是不需要上传文件的一些原因，
    // 2、或者是选择了特殊原因，但是没有上传文件就保存了的情况
    // 根据 reasonType 和 unableUploadReason 进行兜底定位，根据 reasonType 在 REASON_TYPE_REVERSE_MAP 查找对应的坑位
    // 比如 firstFileData 在第一定位逻辑下发现应该是 CertificateType.OnlineVerification ，但是 attachmentList 的元素，CertificateTypeAttachmentMap[CertificateType.OnlineVerification].all().includes(attachmentItem.attachmentFileType)为空
    // 则开始查询 REASON_TYPE_REVERSE_MAP[reasonType] ，就可以 attachmentList 中哪个元素为 CertificateType.OnlineVerification，从而获取到其选择的原因数据，再进行封装数据

    // 第四定位逻辑：
    // 1、如果第三定位逻辑没找到，说明是完全新的数据，执行第四定位逻辑
    // 2、如果第三定位逻辑找到了，但是没有勾选无法上传，说明是刚切换的，直接给默认值

    // 第五定位逻辑：是特殊的情况，手动勾选无法上传，手动赋值-999，则需要重置为undefined

    // 第六定位逻辑：如果是实时变更结束时间，需要先判断是否是毕业证
    const getTargetFileData = ({ index = 'first' }) => {
      const attachmentSourceList = attachmentList || [];
      // 获取对应目标教育类型的，第x个文件坑位的目标文件类型 certificateType
      const { certificateType } = EduTypeCertificateConfigArray[eduType][index];
      // 是毕业证类型
      const isDiploma = certificateType === CertificateType.Diploma;
      // 毕业证的特殊情况
      const isStampGraduateProve =
        // 学历=【"高中（中专）及以下" 】
        diplomaDictIndex === EDUCATION_LEVEL.HIGH_SCHOOL ||
        // 或【 学历≠"高中（中专）及以下" 且 对应的教育经历终止时间早于2001年】
        (diplomaDictIndex !== EDUCATION_LEVEL.HIGH_SCHOOL && endDate < datejs('2001-1-1').valueOf());

      let calculatedAttachmentFileType = null;

      // 执行第一定位逻辑，这种情况下，如果已经是101的特殊情况，确实能找到毕业证结束时间过早的特殊case
      let targetFileArray = attachmentSourceList.filter((attachment) =>
        // 在 certificateType 所对应的 attachmentFileType 集合中，找到与当前 attachmentFileType 相同的元素，就是目标值
        CertificateTypeAttachmentMap[certificateType].all().includes(attachment?.attachmentFileType),
      );

      // TODO 是否需要加这个逻辑，需要PM确认
      // 兜底后端bug逻辑，如果第一定位逻辑执行完毕后，发现length不止为1，那么可能是出现了多个同类型的文件，比如107、108、109，这种正常流程和备用材料都返回的情况
      // 需要对这种进行优先级排序，目前有两种方案：
      // 1、找到有原因的那一项，否则就是默认的一项
      // 2、直接默认使用正常流程文件
      // if (targetFileArray.length > 1) {
      //   const processingScheme = 'DEFAULT_FILE';

      //   if (processingScheme !== 'DEFAULT_FILE') {
      //     // 找到有原因的那一项
      //     const targetFileArrayWithReason = targetFileArray.filter((attachment) => !!attachment?.unableUploadReason);
      //     // 如果找到了有原因的那一项，则展示该项
      //     if (targetFileArrayWithReason.length) {
      //       targetFileArray = targetFileArrayWithReason;
      //     }

      //     // 如果没有原因，则展示当前教育性质下的默认情况文件
      //     else {
      //       targetFileArray = isFullTime
      //         ? CertificateTypeAttachmentMap[certificateType].fullTime[0]
      //         : CertificateTypeAttachmentMap[certificateType].partTime[0];
      //     }
      //   }

      //   // 2、直接默认使用正常流程文件
      //   if (processingScheme === 'DEFAULT_FILE') {
      //     // 区分全日制和非全日制
      //     targetFileArray = isFullTime
      //       ? CertificateTypeAttachmentMap[certificateType].fullTime[0]
      //       : CertificateTypeAttachmentMap[certificateType].partTime[0];
      //   }
      // }

      // 如果第一定位逻辑找完了，并且是毕业证类型，但是有可能是 有时间控制的特殊case，却展示102，先修正，直接跳转第六定位逻辑
      if (targetFileArray.length && isDiploma) {
        const [targetFile] = targetFileArray;
        // attachmentFileType 可能是15、101、102、111、113、114，根据当前数据进行修正
        const { attachmentFileType } = targetFile || {};
        // 如果是101、102、113、114，则需要修正
        if (CertificateTypeAttachmentMap[CertificateType.Diploma].unableUploadAll().includes(attachmentFileType)) {
          // 修正
          if (isFullTime) {
            if (isStampGraduateProve) {
              calculatedAttachmentFileType = AttachmentFileTypeMap.stampGraduateProve;
            } else {
              // 原因选择【毕业证书丢失】的其他情况
              calculatedAttachmentFileType = AttachmentFileTypeMap.electronicRegister;
            }
          }

          if (!isFullTime) {
            if (isStampGraduateProve) {
              calculatedAttachmentFileType = AttachmentFileTypeMap.noFullStampGraduateProve;
            } else {
              calculatedAttachmentFileType = AttachmentFileTypeMap.noFullElectronicRegister;
            }
          }
        }
      }

      // 如果第二定位逻辑没找到，并且 reasonType 有值，则执行第三定位逻辑
      if (!targetFileArray.length) {
        targetFileArray = attachmentSourceList.filter(
          // 找到 reasonIndex 对应的文件类型 和 当前的 fileType 一致的数据，就是目标值
          (attachment) => certificateType === REASON_TYPE_REVERSE_MAP[attachment?.reasonType],
        );

        // 如果这里都还没找到，说明是完全新的数据，或者是选择了无法上传，执行第四定位逻辑
        if (!targetFileArray.length) {
          const isUnableUpload = attachmentSourceList.some(
            (attachment) =>
              // 如果是 -999，说明选择了无法上传
              attachment?.attachmentFileType === SPECIAL_ATTACHMENT_FILE_TYPE,
          );

          if (isUnableUpload) {
            // 第五定位逻辑则是使用undefined
            calculatedAttachmentFileType = undefined;
          } else {
            // 第四定位逻辑则是使用当前默认值即可
            calculatedAttachmentFileType = isFullTime
              ? CertificateTypeAttachmentMap[certificateType].fullTime[0]
              : CertificateTypeAttachmentMap[certificateType].partTime[0];
          }
        } else {
          // 如果执行了第三定位逻辑，就需要把 attachmentFileType 设置为对应的文件类型
          const [targetFile] = targetFileArray;
          const { unableUploadReason } = targetFile || {};
          // 全日制
          if (isFullTime) {
            if (unableUploadReason === REASON_INDEX.DIPLOMA_LOSS) {
              if (isStampGraduateProve) {
                calculatedAttachmentFileType = AttachmentFileTypeMap.stampGraduateProve;
              } else {
                // 原因选择【毕业证书丢失】的其他情况
                calculatedAttachmentFileType = AttachmentFileTypeMap.electronicRegister;
              }
            } else if (unableUploadReason === REASON_INDEX.DEGREE_LOSS) {
              calculatedAttachmentFileType = AttachmentFileTypeMap.certificationReport;
            } else if (unableUploadReason === REASON_INDEX.FOREIGN_AUTH_PENDING) {
              calculatedAttachmentFileType = AttachmentFileTypeMap.degreeScreenshot;
            } else if (unableUploadReason === REASON_INDEX.STUDY_PROOF) {
              calculatedAttachmentFileType = AttachmentFileTypeMap.schoolStudyingProve;
            } else if (unableUploadReason === REASON_INDEX.STUDENT_STATUS_SCREENSHOT) {
              calculatedAttachmentFileType = AttachmentFileTypeMap.studentStatusScreenshot;
            }
          }

          if (!isFullTime) {
            // 非全日制
            if (unableUploadReason === REASON_INDEX.DIPLOMA_LOSS) {
              if (isStampGraduateProve) {
                calculatedAttachmentFileType = AttachmentFileTypeMap.noFullStampGraduateProve;
              } else {
                // 原因选择【毕业证书丢失】的其他情况
                calculatedAttachmentFileType = AttachmentFileTypeMap.noFullElectronicRegister;
              }
            } else if (unableUploadReason === REASON_INDEX.DEGREE_LOSS) {
              calculatedAttachmentFileType = AttachmentFileTypeMap.noFullCertificationReport;
            } else if (unableUploadReason === REASON_INDEX.FOREIGN_AUTH_PENDING) {
              calculatedAttachmentFileType = AttachmentFileTypeMap.noFullDegreeScreenshot;
            } else if (unableUploadReason === REASON_INDEX.STUDY_PROOF) {
              calculatedAttachmentFileType = AttachmentFileTypeMap.noFullSchoolStudyingProve;
            } else if (unableUploadReason === REASON_INDEX.STUDENT_STATUS_SCREENSHOT) {
              calculatedAttachmentFileType = AttachmentFileTypeMap.noFullStudentStatusScreenshot;
            }
          }
        }
      }

      const [targetFile] = targetFileArray;

      const { attachmentId, attachmentName, attachmentUrl, attachmentFileType, ...rest } = targetFile || {};
      const codeItemKey = CertificateCodeItemKeyType[certificateType];

      // 根据 codeItemKey 获取对应的 code 值
      const code = {
        // 比如 codeItemKey 是 educationCode，那么 code 就是 data.educationCode
        [codeItemKey]: data?.[codeItemKey],
      };

      const result = {
        fileId: attachmentId,
        fileName: attachmentName,
        fileUrl: attachmentUrl,
        // 优先使用 calculatedAttachmentFileType，如果为空，则使用 attachmentFileType
        attachmentFileType: calculatedAttachmentFileType || attachmentFileType,
        ...code,
        ...rest,
      };

      return result;
    };

    // 实习生-中国，需要上传教育部学籍在线验证报告
    if (eduType === EduType.InternChinese) {
      const firstFileData = getTargetFileData({
        index: 'first',
      });

      return (
        <Form.Item labelWidth={0} span={11} key={`intern-chinese-${renderKey}`}>
          <UploadCertificate
            labelWidth={labelWidth}
            ref={firstRef}
            data={data}
            isFullTime={isFullTime}
            certificateType={CertificateType.OnlineVerification}
            required
            // 教育部学籍在线验证报告
            value={firstFileData}
            mode={mode}
            onChange={handleChange}
            unableReasonList={unableReasonList}
          />
        </Form.Item>
      );
    }

    // 实习生-非中国，需要上传在读证明
    if (eduType === EduType.InternForeign) {
      const firstFileData = getTargetFileData({
        index: 'first',
      });

      return (
        <Form.Item labelWidth={0} span={11} key={`intern-foreign-${renderKey}`}>
          <UploadCertificate
            labelWidth={labelWidth}
            ref={firstRef}
            data={data}
            isFullTime={isFullTime}
            certificateType={CertificateType.StudyProof}
            required
            // 在读证明
            value={firstFileData}
            mode={mode}
            onChange={handleChange}
            unableReasonList={unableReasonList}
          />
        </Form.Item>
      );
    }

    // 非实习生-中国-专科，需要上传毕业证书和职业资格证书
    if (eduType === EduType.ChineseCollege) {
      const firstFileData = getTargetFileData({
        index: 'first',
      });
      const secondFileData = getTargetFileData({
        index: 'second',
      });

      return (
        <React.Fragment key={`chinese-college-${renderKey}`}>
          <Form.Item labelWidth={0} span={11}>
            <UploadCertificate
              labelWidth={labelWidth}
              ref={firstRef}
              data={data}
              isFullTime={isFullTime}
              certificateType={CertificateType.Diploma}
              required
              // 毕业证书
              value={firstFileData}
              mode={mode}
              onChange={handleChange}
              unableReasonList={unableReasonList}
            />
          </Form.Item>
          <Form.Item labelWidth={0} span={11} offset={rightOffset}>
            <UploadCertificate
              labelWidth={labelWidth}
              ref={secondRef}
              data={data}
              isFullTime={isFullTime}
              certificateType={CertificateType.Professional}
              // 职业资格证书
              value={secondFileData}
              mode={mode}
              onChange={handleChange}
              unableReasonList={unableReasonList}
            />
          </Form.Item>
        </React.Fragment>
      );
    }

    // 常用 case
    // 非实习生-中国-非专科，需要上传毕业证书和学位证书
    if (eduType === EduType.ChineseNonCollege) {
      const firstFileData = getTargetFileData({
        index: 'first',
      });
      const secondFileData = getTargetFileData({
        index: 'second',
      });

      return (
        <React.Fragment key={`chinese-non-college-${renderKey}`}>
          <Form.Item labelWidth={0} span={11}>
            <UploadCertificate
              labelWidth={labelWidth}
              ref={firstRef}
              data={data}
              isFullTime={isFullTime}
              certificateType={CertificateType.Diploma}
              required
              // 毕业证书
              value={firstFileData}
              mode={mode}
              onChange={handleChange}
              unableReasonList={unableReasonList}
            />
          </Form.Item>
          <Form.Item labelWidth={0} span={11} offset={rightOffset}>
            <UploadCertificate
              labelWidth={labelWidth}
              ref={secondRef}
              data={data}
              isFullTime={isFullTime}
              certificateType={CertificateType.Degree}
              required
              // 学位证书
              value={secondFileData}
              mode={mode}
              onChange={handleChange}
              unableReasonList={unableReasonList}
            />
          </Form.Item>
        </React.Fragment>
      );
    }

    // 非实习生-非中国-专科，需要上传毕业证书和留服认证书
    if (eduType === EduType.ForeignCollege) {
      const firstFileData = getTargetFileData({
        index: 'first',
      });
      const secondFileData = getTargetFileData({
        index: 'second',
      });

      return (
        <React.Fragment key={`foreign-college-${renderKey}`}>
          <Form.Item labelWidth={0} span={11}>
            <UploadCertificate
              labelWidth={labelWidth}
              ref={firstRef}
              data={data}
              isFullTime={isFullTime}
              certificateType={CertificateType.Diploma}
              required
              // 毕业证书
              value={firstFileData}
              mode={mode}
              onChange={handleChange}
              unableReasonList={unableReasonList}
            />
          </Form.Item>
          <Form.Item labelWidth={0} span={11} offset={rightOffset}>
            <UploadCertificate
              labelWidth={labelWidth}
              ref={secondRef}
              data={data}
              isFullTime={isFullTime}
              certificateType={CertificateType.ForeignAuth}
              // 留服认证书
              value={secondFileData}
              mode={mode}
              onChange={handleChange}
              unableReasonList={unableReasonList}
            />
          </Form.Item>
        </React.Fragment>
      );
    }

    // 非实习生-非中国-非专科，需要上传毕业证书和留服认证书以及学位证书
    if (eduType === EduType.ForeignNonCollege) {
      const firstFileData = getTargetFileData({
        index: 'first',
      });
      const secondFileData = getTargetFileData({
        index: 'second',
      });
      const thirdFileData = getTargetFileData({
        index: 'third',
      });

      return (
        <React.Fragment key={`foreign-non-college-${renderKey}`}>
          <Form.Item labelWidth={0} span={11}>
            <UploadCertificate
              labelWidth={labelWidth}
              ref={firstRef}
              data={data}
              isFullTime={isFullTime}
              certificateType={CertificateType.Diploma}
              required
              // 毕业证书
              value={firstFileData}
              mode={mode}
              onChange={handleChange}
              unableReasonList={unableReasonList}
            />
          </Form.Item>
          <Form.Item labelWidth={0} span={11} offset={rightOffset}>
            <UploadCertificate
              labelWidth={labelWidth}
              ref={secondRef}
              data={data}
              isFullTime={isFullTime}
              certificateType={CertificateType.Degree}
              // 学位证书
              value={secondFileData}
              mode={mode}
              onChange={handleChange}
              unableReasonList={unableReasonList}
            />
          </Form.Item>
          <Form.Item labelWidth={0} span={11}>
            <UploadCertificate
              labelWidth={labelWidth}
              ref={thirdRef}
              data={data}
              isFullTime={isFullTime}
              certificateType={CertificateType.ForeignAuth}
              required
              // 留服认证书
              value={thirdFileData}
              mode={mode}
              onChange={handleChange}
              unableReasonList={unableReasonList}
            />
          </Form.Item>
        </React.Fragment>
      );
    }

    return null;
  };

  useImperativeHandle(ref, () => ({
    validate,
    getFormData,
    reset,
  }));

  /**
   * 1、初次，educationTypeDictIndex和educationTypeState是undefined，eduType是NotTopDegree，不渲染上传组件
   * 2、数据从后端获取之后，会发现，educationTypeDictIndex和educationTypeState不同，eduType也有值了，子组件会内部先行渲染第一遍
   * 3、然后会执行useEffect，发现educationTypeDictIndex和educationTypeState不同，然后会执行重新渲染，这一步的渲染与第二步的渲染其实是重复的
   */
  // 监听教育类型变化
  useEffect(() => {
    // 如果教育类型发生变化，更新状态并触发重新渲染
    // case1：初始值是undefined，data从后端获取之后，可能会变成 78（或者其他值），然后会执行一次重新渲染
    // case2：后续人为变更，educationTypeDictIndex会变化，然后会执行一次重新渲染
    if (educationTypeDictIndex !== educationTypeState) {
      setEducationTypeState(educationTypeDictIndex);
      // 增加renderKey触发重新渲染，key变动，则子组件会重新渲染
      setRenderKey((prevKey) => prevKey + 1);
    }
  }, [educationTypeDictIndex, educationTypeState]);

  return (
    <Form labelWidth="120px" key={renderKey}>
      {renderUploads()}
    </Form>
  );
});

export default CertificateUploadGroup;
